// Eta.tsx — età dichiarata alla prima apertura (SPECIFICA §3): sotto l'età minima l'app non si usa.
// Resta sul telefono; chi si registra la conferma e il server la controlla di nuovo.
// v019: nuova grafica (personaggio 3D, marchio, tre scelte giorno / mese / anno al posto del calendario).
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar } from "../componenti/Avatar.tsx";
import { useFacciaCasuale } from "../componenti/Faccina.tsx";
import { Marchio } from "../componenti/Marchio.tsx";
import { BASE_API, PERCORSO_PRIVACY } from "../costanti.ts";
import type { ControlloTema } from "../temi/useTema.ts";

/** Anni compiuti (stessa regola del server). null = data non valida. */
export function etaAnni(dataNascita: string, oggi: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dataNascita);
  const o = /^(\d{4})-(\d{2})-(\d{2})$/.exec(oggi);
  if (!m || !o) return null;
  const [a, me, g] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(Date.UTC(a, me - 1, g));
  if (d.getUTCFullYear() !== a || d.getUTCMonth() !== me - 1 || d.getUTCDate() !== g) return null;
  let eta = Number(o[1]) - a;
  if (Number(o[2]) < me || (Number(o[2]) === me && Number(o[3]) < g)) eta--;
  return eta;
}

const due = (n: number) => String(n).padStart(2, "0");

export function Eta({ etaMinima, onConferma }: { tema: ControlloTema; etaMinima: number; onConferma: (data: string) => void }) {
  const { t, i18n } = useTranslation();
  const faccia = useFacciaCasuale();
  const oggi = new Date().toISOString().slice(0, 10);
  const annoOggi = Number(oggi.slice(0, 4));
  const [giorno, setGiorno] = useState("");
  const [mese, setMese] = useState("");
  const [anno, setAnno] = useState("");
  const mesi = useMemo(() => {
    const f = new Intl.DateTimeFormat(i18n.language, { month: "long", timeZone: "UTC" });
    return Array.from({ length: 12 }, (_, i) => f.format(new Date(Date.UTC(2000, i, 15))));
  }, [i18n.language]);
  const anni = useMemo(() => Array.from({ length: 101 }, (_, i) => annoOggi - i), [annoOggi]);
  const data = giorno && mese && anno ? `${anno}-${due(Number(mese))}-${due(Number(giorno))}` : "";
  const eta = data ? etaAnni(data, oggi) : null;
  const valida = eta !== null && eta >= 0 && eta <= 120;

  return (
    <div className="pagina ld-pagina ld-eta">
      <div className="ld-alone" aria-hidden="true" />
      <main className="ld-eta-centro">
        <Avatar quale={faccia} dimensione={190} classe="ld-eta-eroe" />
        <Marchio dimensione="medio" />
        <form
          className="ld-eta-modulo"
          onSubmit={(e) => {
            e.preventDefault();
            if (!valida) return;
            // Si salva anche se l'età non basta: riaprendo l'app resta bloccata (la decide App con etaMinima).
            onConferma(data);
          }}
        >
          <h2 className="ld-titolo ld-centrato">{t("eta.titolo")}</h2>
          <p className="ld-sottotitolo ld-centrato">{t("eta.minimo", { eta: etaMinima })}</p>
          <div className="ld-eta-scelte">
            <label className="ld-eta-campo">
              <span>{t("eta.giorno")}</span>
              <select className="campo" required value={giorno} onChange={(e) => setGiorno(e.target.value)}>
                <option value="" disabled>
                  –
                </option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </label>
            <label className="ld-eta-campo ld-eta-mese">
              <span>{t("eta.mese")}</span>
              <select className="campo" required value={mese} onChange={(e) => setMese(e.target.value)}>
                <option value="" disabled>
                  –
                </option>
                {mesi.map((m, i) => (
                  <option key={i} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <label className="ld-eta-campo">
              <span>{t("eta.anno")}</span>
              <select className="campo" required value={anno} onChange={(e) => setAnno(e.target.value)}>
                <option value="" disabled>
                  –
                </option>
                {anni.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {data && !valida && (
            <p className="admin-errore ld-centrato" role="alert">
              {eta !== null && eta < 0 ? t("eta.data_futura") : t("eta.data_impossibile")}
            </p>
          )}
          <button type="submit" className="ld-cta ld-cta-centrata" disabled={!valida}>
            {t("eta.continua")}
          </button>
          <p className="ld-eta-nota">
            {t("eta.resta_qui")}{" "}
            <a href={`${BASE_API}${PERCORSO_PRIVACY}?lingua=${encodeURIComponent(i18n.language)}`} target="_blank" rel="noopener">
              {t("legale.privacy")}
            </a>
          </p>
        </form>
      </main>
    </div>
  );
}
